import React from "react";
import "./styles.scss";
import {
  BannerSeverity,
  getCurrencySymbol,
  Icon,
  IconName,
  NotificationBanner,
} from "halifax";
import {
  CardPaymentSelectors,
  RewardsPaymentSelectors,
  useCheckoutStateSelector,
} from "@capone/checkout";
import {
  CorpSessionInfo,
  FiatPrice,
  RewardsAccount,
  RewardsPrice,
} from "redmond";
import clsx from "clsx";
import { isCorpTenant } from "@capone/common";
import { PACKAGE_BANNER_EARN_TEXT } from "./textConstants";
import {
  getPackagesShopPricing,
  getQuotedPackageDiscount,
} from "../../state/selectors/common";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";

export interface IPackageDiscountBannerProps {
  discount?: FiatPrice;
  earn?: RewardsPrice;
  earnRewardsAccount?: RewardsAccount;
  isMobile?: boolean;
  isPackageCheckoutOptimizationExperiment?: boolean;
}

export const PackageDiscountBanner = ({
  discount,
  earn,
  earnRewardsAccount,
  isMobile,
  isPackageCheckoutOptimizationExperiment,
}: IPackageDiscountBannerProps) => {
  const { sessionInfo } = React.useContext(ClientContext);
  if (!discount?.value && !earn?.value) return null;

  const isNonPrimaryUser =
    isCorpTenant(config?.TENANT) &&
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role !== "PR";

  if (isNonPrimaryUser) {
    return null;
  }

  return (
    <NotificationBanner
      icon={<Icon name={IconName.PiggyBankCircleOutline} />}
      className={clsx(
        "package-discount-banner",
        {
          "package-checkout-optimization":
            isPackageCheckoutOptimizationExperiment,
        },
        {
          mobile: isMobile,
        }
      )}
      content={PACKAGE_BANNER_EARN_TEXT(discount, earn, earnRewardsAccount)}
      severity={
        isPackageCheckoutOptimizationExperiment
          ? BannerSeverity.NOTICE
          : BannerSeverity.IMPROVEMENT
      }
    />
  );
};

export interface IConnectedPackageDiscountBannerProps {
  isMobile?: boolean;
  isPackageCheckoutOptimizationExperiment?: boolean;
}

export const ConnectedPackageDiscountBanner = ({
  isMobile,
  isPackageCheckoutOptimizationExperiment,
}: IConnectedPackageDiscountBannerProps) => {
  const { sessionInfo } = React.useContext(ClientContext);

  const quotedPackageDiscount = useCheckoutStateSelector(
    getQuotedPackageDiscount
  );
  const packagesShopPricing = useCheckoutStateSelector(getPackagesShopPricing);
  const packageShopDiscount = packagesShopPricing?.totalPackageSavings.fiat;

  const earnForSelectedCard = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForSelectedRewardsAccount
  );
  const earnForHighestEarnAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForHighestEarnRewardsAccount
  );
  const selectedCardPaymentRewardsAccountId = useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodRewardsAccountId
  );
  const highestEarnRewardsAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getHighestEarnAccount
  );

  const rewardsAccountToUse = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsAccounts
  )?.find(
    (account) =>
      account.accountReferenceId ===
      (selectedCardPaymentRewardsAccountId ||
        highestEarnRewardsAccount?.accountReferenceId)
  );

  const earnToUse = earnForSelectedCard || earnForHighestEarnAccount;

  const discount = quotedPackageDiscount
    ? {
        value: quotedPackageDiscount.amount.amount,
        currencyCode: quotedPackageDiscount.amount.currency,
        currencySymbol: getCurrencySymbol(
          quotedPackageDiscount.amount.currency
        ),
      }
    : packageShopDiscount;

  const isNonPrimaryUser =
    isCorpTenant(config?.TENANT) &&
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role !== "PR";

  if (isNonPrimaryUser) {
    return null;
  }

  return (
    <PackageDiscountBanner
      discount={discount}
      earn={earnToUse}
      earnRewardsAccount={rewardsAccountToUse}
      isMobile={isMobile}
      isPackageCheckoutOptimizationExperiment={
        isPackageCheckoutOptimizationExperiment
      }
    />
  );
};
